// ─────────────────────────────────────────────────────────────────────────
// TourProvider — the engine.
//
// Renders a floating popover anchored to a `data-tour="<scope>.<el>"`
// attribute on whatever existing JSX you want to highlight. Steps live
// in tour/scripts.ts; this file is just the runtime.
//
// Key behaviour:
//  - Anchor is found via `document.querySelector('[data-tour="..."]')`.
//  - Popover position uses `getBoundingClientRect()` with viewport math.
//  - `ResizeObserver` + `MutationObserver` re-position on layout shifts.
//  - Missing anchor → silently advance to the next step (dev console.warn).
//  - Existing modal opens → tour pauses (listens for `cima:modal-opened`
//    custom event; also watches body for `[role="dialog"]` mutations).
//  - <768px viewport → popover renders as a bottom-sheet drawer.
//  - Esc exits. Tab cycles inside the popover. ←/→ navigates steps.
//  - Backdrop dims the page but DOES NOT block clicks on the anchor
//    itself (a clip-path cut-out leaves the anchor interactive).
//
// Z-index allocation (existing landscape):
//   navbar              100
//   edit modals         1000-1004
//   sign-off modals     10000
//   action dropdowns    9999
//   TOUR BACKDROP       1005   ← above content, below sign-off
//   TOUR POPOVER        1006
//   WELCOME MODAL       1007   (only at first paint, no other modals)
// ─────────────────────────────────────────────────────────────────────────

import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  ALL_TRACKS, findTrack, PageKey, Role, TourStep, TourTrack, visibleStepsForRole,
} from './scripts';

// Alias so the auto-complete logic reads naturally
const ALL_TRACKS_FOR_AUTO_COMPLETE = ALL_TRACKS;
import {
  getOnboardingState, patchOnboardingState, OnboardingState,
} from '../api/onboarding';

type RoleProp = Role | null | undefined;

interface TourContextValue {
  state:        OnboardingState | null;
  loaded:       boolean;
  isActive:     boolean;
  currentTrack: TourTrack | null;
  currentStep:  TourStep | null;
  stepIndex:    number;
  totalSteps:   number;
  /** Page the host app is currently rendering — used by the help menu. */
  currentPage:  PageKey | null;
  /** Role of the logged-in user — exposed so the help menu can filter. */
  role:         Role | null;
  start:        (trackId: string) => void;
  next:         () => void;
  prev:         () => void;
  exit:         () => void;
  markWelcomeSeen: () => Promise<void>;
  refresh:      () => Promise<void>;
}

const TourContext = createContext<TourContextValue | null>(null);

// localStorage mirror so we can read instantly without waiting on the API
const LS_KEY = 'cima-onboarding-state';

function readLocal(): OnboardingState | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}
function writeLocal(state: OnboardingState) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(state)); } catch {}
}

export function TourProvider({
  role,
  currentPage,
  children,
}: {
  role:        RoleProp;
  currentPage: PageKey | null | undefined;
  children:    React.ReactNode;
}) {
  const [state,        setState]        = useState<OnboardingState | null>(() => readLocal());
  const [loaded,       setLoaded]       = useState(false);
  const [trackId,      setTrackId]      = useState<string | null>(null);
  const [stepIndex,    setStepIndex]    = useState(0);
  const [isPaused,     setIsPaused]     = useState(false);

  // Direction of travel (+1 next / -1 prev). The anchor-missing skip uses it so
  // pressing Back into a not-rendered step skips BACKWARD instead of bouncing
  // forward again (the "can't go Back on the last step" bug).
  const dirRef = useRef<1 | -1>(1);

  // Mirror of `role` we can read from inside callbacks without making them
  // role-dependent (which would recreate the callback on every render and
  // void useCallback's purpose).
  const roleRef = useRef<RoleProp>(role);
  useEffect(() => { roleRef.current = role; }, [role]);

  const track  = trackId ? findTrack(trackId) : null;
  const steps  = useMemo(() => (track ? visibleStepsForRole(track, role || null) : []), [track, role]);
  const step   = steps[stepIndex] || null;
  const active = !!step;

  // Boot — load from backend (localStorage already gave us an instant read)
  const refresh = useCallback(async () => {
    try {
      const remote = await getOnboardingState();
      setState(remote);
      writeLocal(remote);
    } catch {
      // network failure: fall back to local
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Modal-pause integration ────────────────────────────────────────
  useEffect(() => {
    if (!active) return;

    function setPausedIfModalOpen() {
      const anyDialog = document.querySelector('[role="dialog"]:not([data-tour-popover])');
      setIsPaused(!!anyDialog);
    }

    function onOpened() { setIsPaused(true); }
    function onClosed() {
      // small delay so the modal is actually removed from the DOM
      setTimeout(setPausedIfModalOpen, 50);
    }

    document.addEventListener('cima:modal-opened', onOpened);
    document.addEventListener('cima:modal-closed', onClosed);

    const mo = new MutationObserver(setPausedIfModalOpen);
    mo.observe(document.body, { childList: true, subtree: true });

    setPausedIfModalOpen();

    return () => {
      document.removeEventListener('cima:modal-opened', onOpened);
      document.removeEventListener('cima:modal-closed', onClosed);
      mo.disconnect();
    };
  }, [active]);

  // ── Stage-menu driver ──────────────────────────────────────────────
  // The incident workspace's SitRep/CIMT/IAP/Meetings/Timeline tabs live
  // inside a dropdown (the "STAGE x / 4" stepper). Their tour anchors
  // (chat.tab.*) only exist while that menu is open, so open it while such a
  // step is active — otherwise the step's anchor is missing and it silently
  // skips (the "sitrep → iap" gap the tour was jumping).
  useEffect(() => {
    const wantOpen = active && !isPaused && step.anchor.startsWith('chat.tab.');
    // chat.schedulemeeting lives INSIDE the Meetings panel — it only renders
    // when that panel is the active right tab, so ask the workspace to switch
    // (detail.tab). Without this the step's anchor never exists and it skips.
    const wantTab = active && !isPaused && step.anchor === 'chat.schedulemeeting' ? 'meeting' : undefined;
    window.dispatchEvent(new CustomEvent('cima:tour-stagemenu', { detail: { open: wantOpen, tab: wantTab } }));
  }, [active, isPaused, step]);

  // ── Anchor-missing protection — skip empty steps ───────────────────
  useEffect(() => {
    if (!active || isPaused) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const attempt = (elapsed: number) => {
      if (cancelled) return;
      const el = document.querySelector(`[data-tour="${step.anchor}"]`) as HTMLElement | null;
      if (el) {
        // Scroll anchor into view (smooth, only if it's off-screen)
        const rect = el.getBoundingClientRect();
        const offTop    = rect.top    < 80;
        const offBottom = rect.bottom > window.innerHeight - 80;
        if (offTop || offBottom) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      // Not found yet — wait up to step.waitFor ms (e.g. for the stage menu or
      // a panel to mount) before giving up and skipping.
      const budget = step.waitFor ?? 0;
      if (elapsed < budget) {
        timer = setTimeout(() => attempt(elapsed + 80), 80);
        return;
      }
      // eslint-disable-next-line no-console
      console.warn(`[Tour] anchor "${step.anchor}" not found — skipping step "${step.id}"`);
      const d = dirRef.current;
      setStepIndex((i) => {
        const ni = i + d;
        if (ni < 0) return i;                          // going Back with no earlier step — stay
        if (ni >= steps.length) return steps.length;   // forward past the end — ends the tour
        return ni;                                     // skip the missing step in the travel direction
      });
    };
    timer = setTimeout(() => attempt(0), 60);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [active, step, isPaused, stepIndex, steps.length]);

  // ── Lifecycle ──────────────────────────────────────────────────────

  /** Update both React state and localStorage in lock-step, then push to
   *  backend. Optimistic — UI reflects the change before the network call
   *  finishes (or fails). Help menu badges update instantly. */
  const applyProgress = useCallback((id: string, status: 'in_progress' | 'completed' | 'skipped') => {
    setState((s) => {
      const merged: OnboardingState = {
        ...(s || {}),
        tour_progress: { ...(s?.tour_progress || {}), [id]: status },
      };
      // Auto-mark the whole onboarding done if every required track this
      // user can see is now completed. The "chat" track is intentionally
      // excluded from the required set — it depends on having a real
      // incident open, which a brand-new user won't have yet. Without
      // this exclusion the user could complete every other tour and
      // still never see the "Completed" pill.
      const role = roleRef.current;
      const requiredTrackIds = ALL_TRACKS_FOR_AUTO_COMPLETE
        .filter((t) => t.id !== 'chat')
        .filter((t) => !t.roles || !role || t.roles.includes(role as Role))
        .map((t) => t.id);
      const everyDone = requiredTrackIds.every((tid) => (merged.tour_progress || {})[tid] === 'completed');
      const justCompleted = everyDone && !s?.completed_at;
      if (justCompleted) {
        merged.completed_at = new Date().toISOString();
      }
      writeLocal(merged);
      // Schedule backend update. We do it from inside setState so the
      // newly-computed completed_at is included in the patch payload.
      const patch: Partial<OnboardingState> = {
        tour_progress: { [id]: status },
      };
      if (justCompleted) patch.completed_at = merged.completed_at;
      patchOnboardingState(patch).catch(() => {});
      // Confirmatory toast when the WHOLE onboarding flips to done.
      if (justCompleted) {
        setTimeout(() => showTourToast('You\'ve completed every walkthrough. Welcome to CIMA.'), 1400);
      }
      return merged;
    });
  }, []);

  const start = useCallback((id: string) => {
    // Pre-flight — if the first step's anchor doesn't exist, the tour
    // would silently fast-skip every step (looks "stuck" to the user).
    // Surface a clear toast instead of just bailing.
    const track = findTrack(id);
    const candidate = track ? visibleStepsForRole(track, role || null) : [];
    const firstAnchor = candidate[0]?.anchor;
    if (firstAnchor && !document.querySelector(`[data-tour="${firstAnchor}"]`)) {
      // eslint-disable-next-line no-console
      console.warn(`[Tour] cannot start "${id}" — first anchor "${firstAnchor}" not on the current page.`);
      // Show a transient toast at the top of the screen.
      showTourToast(
        id === 'chat'
          ? 'Open any incident first, then re-run "Running an incident" from the ? menu.'
          : `This tour runs on a different page. Navigate there first, then try again.`,
      );
      return;
    }
    dirRef.current = 1;
    setTrackId(id);
    setStepIndex(0);
    applyProgress(id, 'in_progress');
  }, [role, applyProgress]);

  const exit = useCallback(() => {
    const completed = !!trackId && stepIndex >= steps.length - 1;
    if (trackId) {
      applyProgress(trackId, completed ? 'completed' : 'skipped');
      if (completed) {
        // Confirmatory toast — "you've completed the X tour" so the user
        // sees clear feedback the status changed.
        const trackLabel = findTrack(trackId)?.label || 'tour';
        showTourToast(`${trackLabel} complete — status updated.`);
      }
    }
    setTrackId(null);
    setStepIndex(0);
  }, [trackId, stepIndex, steps.length, applyProgress]);

  const next = useCallback(() => {
    dirRef.current = 1;
    if (stepIndex + 1 >= steps.length) { exit(); return; }
    setStepIndex((i) => i + 1);
  }, [stepIndex, steps.length, exit]);

  const prev = useCallback(() => {
    dirRef.current = -1;
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const markWelcomeSeen = useCallback(async () => {
    const now = new Date().toISOString();
    setState((s) => ({ ...(s || {}), welcome_seen_at: now }));
    writeLocal({ ...(state || {}), welcome_seen_at: now });
    try { await patchOnboardingState({ welcome_seen_at: now }); } catch {}
  }, [state]);

  // ── Keyboard nav ───────────────────────────────────────────────────
  useEffect(() => {
    if (!active) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape')     { e.preventDefault(); exit(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft')  { e.preventDefault(); prev(); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, exit, next, prev]);

  const value: TourContextValue = {
    state,
    loaded,
    isActive: active,
    currentTrack: track,
    currentStep: step,
    stepIndex,
    totalSteps: steps.length,
    currentPage: currentPage || null,
    role: role || null,
    start, next, prev, exit,
    markWelcomeSeen,
    refresh,
  };

  return (
    <TourContext.Provider value={value}>
      {children}
      {active && !isPaused && <TourOverlay step={step} index={stepIndex} total={steps.length} onNext={next} onPrev={prev} onExit={exit} />}
    </TourContext.Provider>
  );
}

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used inside <TourProvider>');
  return ctx;
}

// ─────────────────────────────────────────────────────────────────────────
// TourOverlay — backdrop + popover
// ─────────────────────────────────────────────────────────────────────────

const POPOVER_W = 360;
const POPOVER_GAP = 14;

function TourOverlay({
  step, index, total, onNext, onPrev, onExit,
}: {
  step:   TourStep;
  index:  number;
  total:  number;
  onNext: () => void;
  onPrev: () => void;
  onExit: () => void;
}) {
  const [rect, setRect]       = useState<DOMRect | null>(null);
  const [isMobile, setMobile] = useState<boolean>(() => window.innerWidth < 768);
  const popRef = useRef<HTMLDivElement>(null);

  // Track viewport + anchor position. The anchor can mount late — e.g. the
  // stage-menu tab items only exist once the tour opens the menu — so poll for
  // it (up to the step's waitFor budget) instead of giving up immediately.
  // Without this the popover clings to the previous step's (stale) position,
  // landing bottom-centre near the composer instead of beside the tab.
  useEffect(() => {
    let cancelled = false;
    let ro: ResizeObserver | null = null;
    let mo: MutationObserver | null = null;
    let poll: ReturnType<typeof setTimeout> | undefined;
    let onUpdate: (() => void) | null = null;

    const attach = (el: HTMLElement) => {
      onUpdate = () => {
        setRect(el.getBoundingClientRect());
        setMobile(window.innerWidth < 768);
      };
      onUpdate();
      ro = new ResizeObserver(onUpdate);
      ro.observe(el);
      ro.observe(document.body);
      mo = new MutationObserver(onUpdate);
      mo.observe(document.body, { childList: true, subtree: true });
      window.addEventListener('scroll', onUpdate, true);
      window.addEventListener('resize', onUpdate);
    };

    const budget = Math.max(step.waitFor ?? 0, 400) + 200;
    const tryAttach = (elapsed: number) => {
      if (cancelled) return;
      const el = document.querySelector(`[data-tour="${step.anchor}"]`) as HTMLElement | null;
      if (el) { attach(el); return; }
      if (elapsed < budget) poll = setTimeout(() => tryAttach(elapsed + 80), 80);
    };

    setRect(null);        // drop the previous step's position while (re)locating
    tryAttach(0);

    return () => {
      cancelled = true;
      if (poll) clearTimeout(poll);
      ro?.disconnect();
      mo?.disconnect();
      if (onUpdate) {
        window.removeEventListener('scroll', onUpdate, true);
        window.removeEventListener('resize', onUpdate);
      }
    };
  }, [step.anchor]);

  // Focus popover on mount
  useEffect(() => {
    popRef.current?.focus();
  }, [step.id]);

  if (!rect) return null;

  // Popover height estimate — used for off-screen detection. We use a
  // generous upper bound rather than measuring the rendered element so
  // positioning stays deterministic.
  const POPOVER_H = 240;
  const SAFE_PAD = 12;

  // ── Detect "anchor is bigger than the comfy viewport" cases ─────
  // If the anchor is taller than ~70% of the viewport, neither top nor
  // bottom placement fits cleanly — fall back to a centred overlay so
  // the popover is always visible. Same for very wide anchors.
  const anchorTooTall = rect.height > window.innerHeight * 0.7;
  const anchorTooWide = rect.width  > window.innerWidth  * 0.85;
  const useCentered   = !isMobile && (anchorTooTall || anchorTooWide);

  // ── Popover position ─────────────────────────────────────────────
  let top = 0, left = 0, placement: 'top' | 'bottom' | 'left' | 'right' = 'bottom';

  if (isMobile) {
    // Bottom-sheet drawer mode
    top = window.innerHeight - POPOVER_H - SAFE_PAD - 8;
    left = 12;
  } else if (useCentered) {
    // Centred-overlay fallback for giant anchors — popover sits in the
    // top-third of the viewport, horizontally centred. The cut-out
    // highlight still rings the anchor on whatever part is visible.
    top  = Math.max(SAFE_PAD + 64, (window.innerHeight - POPOVER_H) / 2 - 60);
    left = Math.max(SAFE_PAD, (window.innerWidth - POPOVER_W) / 2);
  } else {
    const desired = step.placement && step.placement !== 'auto' ? step.placement : 'bottom';
    placement = desired;

    const fitsBottom = rect.bottom + POPOVER_GAP + POPOVER_H + SAFE_PAD < window.innerHeight;
    const fitsTop    = rect.top    - POPOVER_GAP - POPOVER_H - SAFE_PAD > 0;
    const fitsRight  = rect.right  + POPOVER_GAP + POPOVER_W + SAFE_PAD < window.innerWidth;
    const fitsLeft   = rect.left   - POPOVER_GAP - POPOVER_W - SAFE_PAD > 0;

    // Flip strategy: try the desired side first, then fall back through
    // bottom → top → right → left until something fits.
    const order: typeof placement[] = [placement, 'bottom', 'top', 'right', 'left'];
    const fits: Record<typeof placement, boolean> = {
      bottom: fitsBottom, top: fitsTop, right: fitsRight, left: fitsLeft,
    };
    placement = order.find((p) => fits[p]) || 'bottom';

    switch (placement) {
      case 'bottom':
        top  = rect.bottom + POPOVER_GAP;
        left = rect.left + rect.width / 2 - POPOVER_W / 2;
        break;
      case 'top':
        top  = rect.top - POPOVER_GAP - POPOVER_H;
        left = rect.left + rect.width / 2 - POPOVER_W / 2;
        break;
      case 'right':
        top  = rect.top + rect.height / 2 - POPOVER_H / 2;
        left = rect.right + POPOVER_GAP;
        break;
      case 'left':
        top  = rect.top + rect.height / 2 - POPOVER_H / 2;
        left = rect.left - POPOVER_GAP - POPOVER_W;
        break;
    }

    // Final clamp: popover NEVER renders outside the viewport. Apply on
    // both axes regardless of computed placement so off-screen anchors
    // and weird sizes can't push the popover into the void.
    top  = Math.min(Math.max(SAFE_PAD, top),  window.innerHeight - POPOVER_H - SAFE_PAD);
    left = Math.min(Math.max(SAFE_PAD, left), window.innerWidth  - POPOVER_W - SAFE_PAD);
  }

  // ── Backdrop with a cut-out around the anchor (so it's still clickable visually) ──
  const cutoutPad = 8;
  const cutoutPath = isMobile || useCentered
    ? '' // no cut-out — full backdrop, popover floats free of the anchor
    : `polygon(
        0 0, 100% 0, 100% 100%, 0 100%, 0 0,
        ${rect.left - cutoutPad}px ${rect.top - cutoutPad}px,
        ${rect.left - cutoutPad}px ${rect.bottom + cutoutPad}px,
        ${rect.right + cutoutPad}px ${rect.bottom + cutoutPad}px,
        ${rect.right + cutoutPad}px ${rect.top - cutoutPad}px,
        ${rect.left - cutoutPad}px ${rect.top - cutoutPad}px
      )`;

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden="true"
        style={{
          position:    'fixed', inset: 0, zIndex: 1005,
          background:  'rgba(15, 23, 42, 0.55)',
          pointerEvents: 'auto',
          clipPath:    cutoutPath,
          WebkitClipPath: cutoutPath,
          transition:  'clip-path 0.25s ease-out',
        }}
        onClick={onExit}
      />
      {/* Anchor highlight ring (purely cosmetic — fits over the cut-out) */}
      {!isMobile && !useCentered && (
        <div
          aria-hidden="true"
          style={{
            position:  'fixed', zIndex: 1005,
            top:       rect.top - cutoutPad,
            left:      rect.left - cutoutPad,
            width:     rect.width + cutoutPad * 2,
            height:    rect.height + cutoutPad * 2,
            borderRadius: 10,
            boxShadow: '0 0 0 2px rgba(255,255,255,0.85), 0 0 0 4px rgba(99,55,138,0.55), 0 0 24px rgba(99,55,138,0.35)',
            pointerEvents: 'none',
            transition: 'top 0.25s, left 0.25s, width 0.25s, height 0.25s',
          }}
        />
      )}
      {/* Popover */}
      <div
        ref={popRef}
        role="dialog"
        data-tour-popover="true"
        aria-modal="false"
        aria-labelledby={`tour-title-${step.id}`}
        tabIndex={-1}
        style={{
          position:    'fixed', zIndex: 1006,
          top:         isMobile ? 'auto' : top,
          bottom:      isMobile ? 12 : 'auto',
          left,
          width:       isMobile ? 'calc(100vw - 24px)' : POPOVER_W,
          background:  'var(--card)',
          border:      '1px solid var(--border)',
          borderRadius: 14,
          boxShadow:   '0 16px 48px rgba(15,23,42,0.28), 0 4px 12px rgba(15,23,42,0.12)',
          padding:     '18px 20px 16px',
          color:       'var(--fg)',
          animation:   'cimaTourIn 0.25s ease-out',
          fontFamily:  'inherit',
        }}
      >
        {/* Inject keyframes once */}
        <style>{`
          @keyframes cimaTourIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
          @media (prefers-reduced-motion: reduce) { @keyframes cimaTourIn { from, to { opacity: 1; transform: none; } } }
        `}</style>

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 10,
        }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            padding: '3px 10px', borderRadius: 999,
            background: 'rgba(99,55,138,0.12)', color: 'var(--primary)',
            fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase',
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: '50%', background: 'var(--primary)',
              animation: 'cimaCriticalDotPulse 1.8s infinite',
            }} />
            Step {index + 1} of {total}
          </div>
          <button
            onClick={onExit}
            aria-label="Exit tour"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: 4, color: 'var(--muted)', fontSize: 16, lineHeight: 1,
              borderRadius: 6,
            }}
          >×</button>
        </div>

        <h3 id={`tour-title-${step.id}`} style={{
          margin: '0 0 6px', fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em',
          color: 'var(--fg)',
        }}>{step.title}</h3>

        <p style={{
          margin: '0 0 14px', fontSize: 13.5, lineHeight: 1.55, color: 'var(--fg-soft)',
        }} dangerouslySetInnerHTML={{ __html: renderInlineMd(step.body) }} />

        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <button
            onClick={onExit}
            style={{
              background: 'transparent', color: 'var(--muted)',
              border: 'none', fontFamily: 'inherit', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', padding: '7px 4px',
            }}
          >Skip tour</button>
          <div style={{ flex: 1 }} />
          {index > 0 && (
            <button
              onClick={onPrev}
              style={{
                background: 'var(--bg)', color: 'var(--fg)',
                border: '1px solid var(--border)', borderRadius: 8,
                padding: '7px 12px', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600,
                cursor: 'pointer',
              }}
            >Back</button>
          )}
          <button
            onClick={onNext}
            style={{
              background: 'var(--primary)',
              color: 'white', border: 'none', borderRadius: 8,
              padding: '7px 14px', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(99,55,138,0.28)',
            }}
          >{step.ctaLabel || (index + 1 >= total ? 'Finish' : 'Next')}</button>
        </div>

        {/* Progress dots */}
        <div style={{
          display: 'flex', gap: 4, marginTop: 12, justifyContent: 'center',
        }}>
          {Array.from({ length: total }).map((_, i) => (
            <span
              key={i}
              style={{
                width:  i === index ? 18 : 5,
                height: 5, borderRadius: 3,
                background: i === index ? 'var(--primary)' : i < index ? 'var(--primary-border)' : 'var(--border)',
                transition: 'all 0.25s',
              }}
            />
          ))}
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// showTourToast — transient floating message, used when a tour can't start
// (e.g. the user clicked "Running an incident" from the dashboard). Built
// as a vanilla DOM node so it works regardless of React state.
// ─────────────────────────────────────────────────────────────────────────
function showTourToast(message: string) {
  // Reuse existing toast if one is on-screen
  let host = document.getElementById('cima-tour-toast');
  if (host) host.remove();
  host = document.createElement('div');
  host.id = 'cima-tour-toast';
  host.setAttribute('role', 'status');
  host.setAttribute('aria-live', 'polite');
  host.style.cssText = `
    position: fixed; z-index: 1008; top: 80px; left: 50%;
    transform: translateX(-50%) translateY(-8px);
    background: #0153D4;
    color: white; font-family: inherit; font-size: 13.5px; font-weight: 600;
    padding: 12px 18px; border-radius: 10px; max-width: 460px;
    box-shadow: 0 12px 32px rgba(1, 39, 112, 0.36);
    display: flex; align-items: center; gap: 10px;
    opacity: 0; transition: opacity 0.2s, transform 0.2s;
  `;
  host.innerHTML = `
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M20 6 9 17l-5-5"/></svg>
    <span>${message.replace(/</g, '&lt;')}</span>
  `;
  document.body.appendChild(host);
  // Animate in
  requestAnimationFrame(() => {
    host!.style.opacity = '1';
    host!.style.transform = 'translateX(-50%) translateY(0)';
  });
  // Auto-dismiss
  setTimeout(() => {
    if (!host) return;
    host.style.opacity = '0';
    host.style.transform = 'translateX(-50%) translateY(-8px)';
    setTimeout(() => host?.remove(), 250);
  }, 4500);
}

// Tiny inline markdown — just **bold** and `code`. Same subset MarkdownText
// supports. Keeps the popover self-contained.
function renderInlineMd(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code style="background:rgba(0,103,254,0.08);padding:1px 5px;border-radius:3px;font-size:0.9em;">$1</code>');
}
